import React from "react";
import { Form } from "react-final-form";
import { useHistory, useParams } from "react-router-dom";
import { useLocation } from "react-router";
import { queryCache, useQuery } from "react-query";
import makeStyles from "@material-ui/core/styles/makeStyles";
import Container from "@material-ui/core/Container";
import { Divider } from "@material-ui/core";
import Loading from "../../../components/Loading";
import Button from "../../../components/Button";
import { useApplicationConfig } from "../../application/modals/hooks/useApplicationConfig";
import { EducationType } from "../../school/constants/types";
import { useWizard } from "../../wizard/context";
import { RestrictedCountriesWidget } from "../components/ViewMorePage/RestrictedCountriesWidget";
import { RequiredDocumentsWidget } from "../components/ViewMorePage/RequiredDocumentsWidget";
import { ProgramSubInfoWidget } from "../components/ViewMorePage/ProgramSubInfoWidget";
import { ViewMoreDetailsWidget } from "../components/ViewMorePage/ViewMoreDetailsWidget";
import { ExtraDocumentsWidget } from "../components/ViewMorePage/ExtraDocumentsWidget";
import { SchoolInfoWidget } from "../components/ViewMorePage/SchoolInfoWidget";
import { BranchesWidget } from "../components/ViewMorePage/BranchesWidget";
import { FeesWidget } from "../components/ViewMorePage/FeesWidget";
import { IntakesWidget } from "../components/ViewMorePage/IntakesWidget";
import { parseToViewMoreFrontEndModel } from "../helpers/parseToViewMoreFrontEndModel";
import { ExamsWidget } from "../components/ViewMorePage/ExamsWidget";
import useProgramQueriesSecondary from "../hooks/useProgramQueriesSecondary";
import useProgramQueriesHigher from "../hooks/useProgramQueriesHigher";
import { useAuth } from "../../auth/hooks/useAuth";
import { UserRole } from "../../user/enums/UserRole";
import { Routes } from "../../../constants/routes";
import { urlReplacer } from "../../../utils";
import { Helmet } from "react-helmet";
import { endOfTitle } from "../../../constants/constants";
import Error from "../../../components/Error";
import useStudentQueries from "../../student/hooks/useStudentQueries";
import { DetailType } from "../constants";

const useStyles = makeStyles(() => ({
  main: {
    display: "flex",
    justifyContent: "center",
  },
  root: {
    width: 1144,
    border: "1px solid #e8eaf0",
    background: "#FFFFFF",
    boxShadow: "0px 0px 8px #C2C9D6",
    borderRadius: 15,
  },
  content: {
    padding: "16px 32px",
  },
  divider: {
    backgroundColor: "#DBDBDB",
  },
  subContainer: {
    marginTop: 42,
  },
  extraDocumentsContainer: {
    marginTop: 56,
  },
  applyButtonContainer: {
    marginTop: 48,
    marginBottom: 42,
    display: "flex",
    justifyContent: "right",
  },
  applyButton: {
    width: 235,
    height: 44,
  },
}));

export const ProgramViewMorePage = () => {
  const classes = useStyles();

  const history = useHistory();
  const { pathname } = useLocation();
  const params: { educationType: string; id: string } = useParams();
  const { fetchViewMore: fetchViewMoreHigher } = useProgramQueriesHigher();
  const { fetchViewMore: fetchViewMoreSecondary } =
    useProgramQueriesSecondary();
  const { fetchCredits } = useStudentQueries();
  const { openWizard } = useWizard();
  const { getCreateApplicationConfig, getNoCreditsConfig } =
    useApplicationConfig();
  const { user } = useAuth();
  const isStudent = user.role === UserRole.Student;
  const isParent = user.role === UserRole.Parent;
  const isAdminOrSupport = [
    UserRole.Owner,
    UserRole.Admin,
    UserRole.Support,
  ].includes(user.role);

  let fetchViewMore;

  switch (+params.educationType) {
    case EducationType.Higher:
      fetchViewMore = fetchViewMoreHigher;
      break;
    case EducationType.Secondary:
      fetchViewMore = fetchViewMoreSecondary;
      break;
  }

  const { isLoading, data } = useQuery<any>(`${params.id}`, fetchViewMore);

  if (isLoading) {
    return <Loading />;
  }

  const applyAs = async () => {
    if (isStudent || isParent) {
      const {
        data: { availableCredits, package: currentPackage },
      } = await fetchCredits();

      const hasPackage = currentPackage !== null;
      const hasCredits = availableCredits > 0;

      if (!hasPackage || !hasCredits) {
        if (await openWizard(getNoCreditsConfig(hasPackage))) {
          history.push(
            `/${Routes.EXTRA_SERVICES}?pack=2&redirectUrl=${pathname}`,
          );
        }

        return;
      }
    }

    const result = await openWizard(
      getCreateApplicationConfig(params.id, +params.educationType),
    );

    if (result) {
      await queryCache.invalidateQueries();
      const applicationRoute = isStudent
        ? `/0/${Routes.APPLICATIONS}`
        : urlReplacer(`/0/${Routes.STUDENT}/${Routes.APPLICATIONS}`, {
            id: result.studentId,
          });

      history.push(applicationRoute);
    }
  };

  const program = data ? parseToViewMoreFrontEndModel(data.data) : null;
  const isSecondary = program?.school?.typeId === EducationType.Secondary;

  return (
    <Container maxWidth="lg" className={classes.main}>
      <Helmet>
        <title>View More{endOfTitle}</title>
      </Helmet>
      {!program ? (
        <Error text={"The program was not found"} />
      ) : (
        <div className={classes.root}>
          <SchoolInfoWidget
            school={program.school}
            isMasked={program.isMasked}
            row0={{
              text0: program.school.name,
            }}
            row1={{
              text0: program.majorName || program.levelName,
              text1: program.grade,
            }}
            row2={{
              text0: program.degreeName,
              text1: isSecondary ? "School Type - " : "",
              subText1: program.schoolTypeName,
            }}
            row3={{
              text0: isSecondary ? "Housing Type - " : "",
              subText0: program.housingTypeNames ?? "N/A",
            }}
          />
          <Divider className={classes.divider} />
          <div className={classes.content}>
            <ProgramSubInfoWidget
              schoolCountry={program.school.country}
              programType={program.programType}
              programDuration={program.duration}
            />
            <div className={classes.subContainer}>
              <RequiredDocumentsWidget requiredDocs={program.requiredDocs} />
            </div>
            <div className={classes.subContainer}>
              <IntakesWidget intakes={program.intakes} />
            </div>
            <div className={classes.subContainer}>
              <Form
                onSubmit={() => undefined}
                render={() => (
                  <FeesWidget fees={program.fees} isMasked={program.isMasked} />
                )}
              />
            </div>
            <div className={classes.subContainer}>
              <BranchesWidget branches={program.branches} />
            </div>
            {!!program.exams.length && (
              <div className={classes.subContainer}>
                <ExamsWidget exams={program.exams} />
              </div>
            )}
            {!!program.restrictedCountries?.length && (
              <div className={classes.subContainer}>
                <RestrictedCountriesWidget
                  restrictedCountries={program.restrictedCountries}
                />
              </div>
            )}
            <div className={classes.subContainer}>
              <ViewMoreDetailsWidget
                programDetails={program.details}
                isMasked={program.isMasked}
                notMaskedFields={[
                  DetailType.TuitionAndFees,
                  DetailType.OtherFees,
                  DetailType.DepositAndRefundPolicy,
                ]}
              />
            </div>
            {!!program.extraDocuments.length && (
              <div className={classes.extraDocumentsContainer}>
                <ExtraDocumentsWidget extraDocuments={program.extraDocuments} />
              </div>
            )}
            <div className={classes.applyButtonContainer}>
              <Button
                className={classes.applyButton}
                type={"submit"}
                disabled={isAdminOrSupport}
                variant={"contained"}
                color={"secondary"}
                fullWidth
                onClick={applyAs}
              >
                {isStudent ? "Apply" : "Apply As"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Container>
  );
};
